<script setup lang="ts">
import SearcherViewPresenter from "@/utils/common/SearcherViewPresenter.ts";
import CriteriaView from "@/views/CriteriaView.vue";
import {computed, onBeforeMount, Ref, ref} from "vue";
import Translator from "@/utils/common/Translator.ts";
import {GetRowIdParams, GridReadyEvent, PaginationChangedEvent, RowClickedEvent} from "ag-grid-community";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import {AgGridVue} from "ag-grid-vue3";
import RefreshIcon from "@/assets/editViewIcons/RefreshIcon.vue";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import MetaData from "@/utils/common/MetaData.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import TextFieldWidget from "@/components/EditView/widgets/TextFieldWidget.vue";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import ReferenceFieldWidgetUtil from "@/utils/editView/widgets/ReferenceFieldWidgetUtil.ts";
import ReferenceGridFieldWidgetUtil from "@/utils/editView/widgets/ReferenceGridFieldWidgetUtil.ts";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import {QSelect} from "quasar";
import {ColumnVisibleEvent, RowSelectionOptions} from "ag-grid-enterprise";
import AgShowFieldInfoContextMenu from "@/utils/common/AgShowFieldInfoContextMenu.ts";
import EnterIcon from "@/assets/editViewIcons/EnterIcon.vue";
import QuickFilterGroups from "@/components/listView/QuickFilterGroups.vue";
import ExtraCriteriaWidgets from "@/views/ExtraCriteriaWidgets.vue";
import CriteriaField = namaTypes.CriteriaField;

let props = defineProps({
  fieldUtil: {
    type: FieldWidgetUtil,
    required: true
  },
  isFieldEnabled: {
    type: Boolean,
    required: false,
    default: true
  }
});

const agShowFieldInfoContextMenu = new AgShowFieldInfoContextMenu()
const emit = defineEmits(['closeDialog']);
const util = props.fieldUtil as ReferenceFieldWidgetUtil | ReferenceGridFieldWidgetUtil;
const presenter = new SearcherViewPresenter(util);
const criteriaTree = presenter.criteriaTree;
const criteriaFields: Ref<CriteriaField[]> = ref([]);
const paginationPageSize = presenter.paginationPageSize;
const isGridReady = ref(false);
const cacheBlockSize = computed(() => {
  return paginationPageSize.value < 100 ? paginationPageSize.value * 2 : paginationPageSize.value
});
const paginationPageSizeSelector = ref([25, 50, 100, 250, 1000, 2500, 5000, 50000]);
const rowSelection: RowSelectionOptions = {
  mode: "multiRow",
  checkboxes: presenter.isGridReferenceField,
  headerCheckbox: presenter.isGridReferenceField
};
const {compactMode} = storeToRefs(useNamaFlagsStore());
const displayedSearchInOptions: Ref<Record<string, string>[]> = ref([]);
const INSERT_MESSAGE = "{0} Rows have been Inserted Successfully";
const isFilterEmpty = computed(() => ListViewUtils.isFilterEmpty(presenter.criteriaTree.value));

onBeforeMount(async () => {
  await presenter.start();
  criteriaFields.value = presenter.criteriaFields() ?? [];
  isGridReady.value = true;
});

function onGridReady(params: GridReadyEvent) {
  presenter.gridApi.value = params.api;
  presenter.gridApi.value!.setGridOption("serverSideDatasource", presenter.createServerSideDatasource());
  useMessagesStore().pushMessage({message: AGGridUtils.autoLoadDisabledMsg, warning: true});
}

async function onCriteriaViewEnterDown() {
  await FieldWidgetUtil.saveEditingFieldsAndGrids();
  presenter.preventAutoLoad.value = false;
  presenter.gridApi.value?.refreshServerSide({purge: true});
}

function updateGridData() {
  presenter.gridApi.value!.refreshServerSide({purge: true});
}

function onUpdateSearchColumnFields(newValue: Record<string, string>) {
  presenter.searchInColumnsValue.value = newValue;
  if (presenter.searchValue)
    updateGridData()
}

function onFilterInSearchInOption(val, update) {
  update(() => ListViewUtils.filterSortOption(val, displayedSearchInOptions, presenter.searchColumnOptions),
    ref => {
      if (val !== '' && ref.options.length > 0) {
        ref.setOptionIndex(-1)
        ref.moveOptionSelection(1, true)
      }
    })
}

async function onRowClicked(event: RowClickedEvent) {
  if (GUIUtilities.elementOrParentHasClass(event.event?.target as HTMLElement | null, "nama-none-acc"))
    return;
  if ((event.event! as PointerEvent).ctrlKey)
    return;
  if (!props.isFieldEnabled)
    return;
  await util.onSelect(event.data);
  if (presenter.isGridReferenceField)
    await util.saveChanges(true);
  emit('closeDialog');
}

function resetRowsAfterInsert() {
  let api = presenter.gridApi.value!;
  let rowData = AGGridUtils.getCurrentPageData(api);
  rowData?.forEach(l => l[ListViewUtils.REPEAT_COUNT_COLUMN_ID] = 0);
  api.setGridOption("rowData", rowData);
  api.redrawRows();
  api.deselectAll();
}

async function onOkClicked(isInsert?: boolean) {
  let api = presenter.gridApi.value!;
  let selectedRows = AGGridUtils.fetchSelectedRows(api!);
  api?.getColumnDef(ListViewUtils.REPEAT_COUNT_COLUMN_ID);
  if (!selectedRows || !selectedRows.length) {
    emit('closeDialog');
    return;
  }
  await (util as ReferenceGridFieldWidgetUtil).onSelectMultiple(selectedRows);
  if (!isInsert) {
    emit('closeDialog');
  } else {
    useMessagesStore().pushMessageStr(Translator.translateMessage(INSERT_MESSAGE, selectedRows.length + ""));
    resetRowsAfterInsert();
  }
}

async function onInsertAtEndClicked() {
  let selectedRows = AGGridUtils.fetchSelectedRows(presenter.gridApi.value!);
  if (!selectedRows || !selectedRows.length) {
    emit('closeDialog');
    return;
  }
  await (util as ReferenceGridFieldWidgetUtil).onInsertAtEndClicked(selectedRows);
  useMessagesStore().pushMessageStr(Translator.translateMessage(INSERT_MESSAGE, selectedRows.length + ""));
  resetRowsAfterInsert();
}

async function onInsertAtEndByOrderClicked() {
  let selectedRows = AGGridUtils.fetchSelectedRows(presenter.gridApi.value!);
  if (!selectedRows || !selectedRows.length) {
    emit('closeDialog');
    return;
  }
  await (util as ReferenceGridFieldWidgetUtil).onInsertAtEndByOrderClicked(selectedRows);
  useMessagesStore().pushMessageStr(Translator.translateMessage(INSERT_MESSAGE, selectedRows.length + ""));
  resetRowsAfterInsert();
}

function onVisibilityChanged(event: ColumnVisibleEvent) {
  if (!event.visible || !presenter.gridApi.value)
    return;
  presenter.gridApi.value.refreshServerSide();
}

function onPaginationChanged(event: PaginationChangedEvent) {
  if (event.newPageSize) {
    paginationPageSize.value = event.api.paginationGetPageSize();
  }
}

defineExpose({onOkClicked, onInsertAtEndClicked, onInsertAtEndByOrderClicked});
</script>

<template>
  <q-card-section class="tw-h-full tw-w-full tw-p-0" :data-entity-type="presenter.entityType">
    <div class="tw-w-full tw-h-full tw-flex tw-flex-col tw-gap-2">
      <div class="tw-w-full tw-flex tw-gap-[1%] tw-items-start mobile:tw-flex-col mobile:tw-gap-[5px]">
        <TextFieldWidget :presenter="presenter" :field="{id: SearcherViewPresenter.VALUE_TO_SEARCH}" :is-inside-composite="true"
                         :custom-place-holder="Translator.translateId('searchInEntity')"
                         :field-meta-data="MetaData.createTextFieldMetaData(SearcherViewPresenter.VALUE_TO_SEARCH)"/>
        <q-select
          outlined dense :options-dense="compactMode"
          :model-value="presenter.searchInColumnsValue.value"
          @update:model-value="onUpdateSearchColumnFields"
          @filter="onFilterInSearchInOption"
          :placeholder="Translator.translateId('sortOn')"
          :options="displayedSearchInOptions"
          class="tw-w-1/4 nama-criteria-field-input nama-customize-list-filter-dense-input mobile:tw-w-full"
          input-class="tw-bg-transparent tw-px-0 focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-white
              dark:tw-placeholder-gray-300 header-search-in-popup-input"
        />
      </div>
      <div class="tw-w-[90%] tw-flex tw-gap-[1%] mobile:tw-w-full tw-items-start mobile:tw-flex-col mobile:tw-gap-[5px]">
        <ExtraCriteriaWidgets :presenter="presenter" :sortDirections="presenter.sortDirections" @updateGridData="updateGridData" :isListView="false"/>
      </div>
      <div class="tw-w-full tw-h-fit" v-if="isGridReady">
        <CriteriaView v-model:criteria-tree="criteriaTree" :criteria-fields="criteriaFields" :owner-entity="presenter.entityType" :isFilterEmpty="isFilterEmpty"
                       @enterDown="onCriteriaViewEnterDown" :onCriteriaViewEnterDown="onCriteriaViewEnterDown">
          <a
            class="tw-bg-gray-200 hover:tw-bg-gray-300 active:tw-bg-gray-400 dark:tw-bg-gray-600 dark:hover:tw-bg-gray-500 dark:active:tw-bg-gray-400 tw-p-2 tw-rounded-lg tw-cursor-pointer"
            @click="onCriteriaViewEnterDown" :title="Translator.translateId(BusinessFunction.refresh)">
            <RefreshIcon class="tw-size-5 !tw-fill-[#606060] dark:!tw-fill-white"/>
          </a>
        </CriteriaView>
      </div>
      <a v-if="!isFilterEmpty"
         class="tw-bg-primary-light hover:tw-opacity-90 active:tw-opacity-95 dark:tw-bg-primary-dark tw-p-2 tw-rounded-lg tw-cursor-pointer tw-w-[100px] mobile:tw-w-full tw-mr-5 mobile:tw-mr-0 tw-self-end"
         @click="onCriteriaViewEnterDown" :title="Translator.translateId(BusinessFunction.refresh)">
        <EnterIcon class="tw-size-4 mobile:tw-size-5 !tw-fill-white tw-justify-self-center"/>
      </a>
      <QuickFilterGroups :presenter="presenter" class="tw-py-2" v-if="presenter.quickFilterGroup.value && !!presenter.quickFilterGroup.value.length"/>
      <AgGridVue
        class="tw-grow"
        style="width: 100%; font-size: var(--ag-font-size);"
        :getContextMenuItems="agShowFieldInfoContextMenu.getContextMenuItems.value"
        :getMainMenuItems="agShowFieldInfoContextMenu.getMainMenuItems.value"
        rowModelType="serverSide"
        :columnDefs="presenter.columnDefs.value"
        v-if="isGridReady"
        :rowSelection="rowSelection"
        :cellSelection="true"
        :rowNumbers="true"
        :pagination="true"
        :paginationPageSize="paginationPageSize"
        :paginationPageSizeSelector="paginationPageSizeSelector"
        :enableRtl="Translator.isLanguageRTL()"
        :localeText="Translator.fetchAGGridLocale()"
        :cacheBlockSize="cacheBlockSize"
        :serverSideInitialRowCount="15000"
        :autoSizeStrategy='{type: "fitCellContents"}'
        :getRowId="(params: GetRowIdParams) => params.data.namaRowIndex"
        :sendToClipboard="AGGridUtils.sendToClipboard"
        @grid-ready="onGridReady"
        @rowClicked="onRowClicked"
        @columnVisible="onVisibilityChanged"
        @paginationChanged="onPaginationChanged"
        :enableCellTextSelection="true"
        :processCellForClipboard="AGGridUtils.processCellForClipboard"
      ></AgGridVue>
    </div>
  </q-card-section>
</template>
